import appStore from '@/store'
import { getToken } from '@/utils/auth'

const DEFAULT_TTL = 10 * 60 * 1000
const MAX_ENTRIES = 100
let entries = new Map()
let scope = ''
let nextId = 0

// Match request.js: never reuse data across languages or signed-in accounts.
function syncScope() {
  const current = JSON.stringify([appStore.getters.language || 'en', getToken() || ''])
  if (current !== scope) {
    entries.clear()
    scope = current
  }
}

function stable(value) {
  if (Array.isArray(value)) return value.map(stable)
  if (value && typeof value === 'object') {
    return Object.keys(value).sort().reduce((result, key) => {
      if (value[key] !== undefined) result[key] = stable(value[key])
      return result
    }, {})
  }
  return value
}

function makeKey(namespace, args) {
  return namespace + ':' + JSON.stringify(stable(args))
}

// API responses are JSON. Each caller gets its own copy so UI mutations cannot
// contaminate cached data (for example category trees and selected flags).
function copy(value) {
  return value === undefined ? value : JSON.parse(JSON.stringify(value))
}

/** Wrap a Promise-returning JSON API without invoking it to discover its URL.
 * ttl=0 merges simultaneous requests only; completed results are never cached.
 * namespace is the API URL, used by clearCache(url, params).
 */
export function createCachedApi(apiFn, ttl = DEFAULT_TTL, namespace) {
  const name = namespace || 'api-' + (++nextId)
  return function(...args) {
    syncScope()
    gcCache()
    const key = makeKey(name, args.length ? args : [{}])
    const existing = entries.get(key)
    if (existing) return existing.promise.then(copy)

    const entry = { namespace: name, pending: true, expiresAt: Infinity }
    const promise = Promise.resolve().then(() => apiFn.apply(this, args)).then(result => {
      // Ignore completions belonging to an invalidated cache or previous account.
      if (entries.get(key) === entry) {
        if (ttl <= 0 || (result && result.code != null && Number(result.code) !== 200)) {
          entries.delete(key)
        } else {
          entry.pending = false
          entry.expiresAt = Date.now() + ttl
        }
      }
      return copy(result)
    }).catch(error => {
      if (entries.get(key) === entry) entries.delete(key)
      throw error
    })
    entry.promise = promise
    if (entries.size >= MAX_ENTRIES) {
      const completed = [...entries].find(([, item]) => !item.pending)
      if (completed) entries.delete(completed[0])
    }
    // Keep concurrent-request tracking bounded as well.
    if (entries.size < MAX_ENTRIES) entries.set(key, entry)
    return promise.then(copy)
  }
}

export function clearCache(url, params) {
  if (params !== undefined) {
    entries.delete(makeKey(url, [params]))
  } else {
    entries.forEach((entry, key) => {
      if (entry.namespace === url) entries.delete(key)
    })
  }
}

export function clearAllCache() {
  entries.clear()
}

export function gcCache() {
  const now = Date.now()
  entries.forEach((entry, key) => {
    if (!entry.pending && now >= entry.expiresAt) entries.delete(key)
  })
}
